// src/components/ui/CircularReveal.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CircularRevealProps {
  isOpen: boolean;
  onAnimationComplete?: () => void;
  originX?: number | string; // Can be percentage (e.g. "50%") or pixel value
  originY?: number | string;
  className?: string;
  bgClassName?: string;
  children?: React.ReactNode;
  zIndex?: number;
}

/**
 * A reusable component that creates a circular reveal/hide animation
 * similar to the one used in the navbar menu.
 */
const CircularReveal: React.FC<CircularRevealProps> = ({
  isOpen,
  onAnimationComplete,
  originX = 'calc(100% - 3rem)',
  originY = '3rem',
  className,
  bgClassName = 'bg-white',
  children,
  zIndex = 40,
}) => {
  // Animation variants for the circular reveal effect
  const revealVariants = {
    hidden: {
      opacity: 0,
      clipPath: `circle(0% at ${originX} ${originY})`,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
    visible: {
      opacity: 1,
      clipPath: `circle(150% at ${originX} ${originY})`,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 30,
        duration: 0.7,
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn(
            'fixed inset-0 flex flex-col justify-center items-center',
            bgClassName,
            className,
          )}
          style={{ zIndex }}
          variants={revealVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onAnimationComplete={
            isOpen && onAnimationComplete
              ? () => onAnimationComplete()
              : undefined
          }>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CircularReveal;
